import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  useTheme,
  alpha,
  styled,
  Paper,
  Fade,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import GroupIcon from "@mui/icons-material/Group";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

// Ícone estilizado
const StyledIconBox = styled(Box)(({ theme, color }) => ({
  width: 56,
  height: 56,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  transition: "background-color 0.3s ease, transform 0.3s ease",
  "& svg": {
    fontSize: 28,
    color: color || theme.palette.primary.main,
  },
}));

// Array de características
const features = [
  {
    icon: <LocalHospitalIcon />,
    title: "Ampla Cobertura",
    description:
      "Acesso a consultas, exames, internações e cirurgias em todo o território nacional.",
    color: "#00897B", // Teal
    delay: 200,
  },
  {
    icon: <GroupIcon />,
    title: "Planos Individuais",
    description:
      "Soluções personalizadas para proteger sua saúde com flexibilidade e economia.",
    color: "#4CAF50", // Verde
    delay: 400,
  },
  {
    icon: <Diversity3Icon />,
    title: "Planos Familiares",
    description:
      "Proteção completa para toda a sua família com condições e descontos especiais.",
    color: "#FFB300", // Âmbar
    delay: 600,
  },
  {
    icon: <BusinessCenterIcon />,
    title: "Planos Empresariais",
    description:
      "Benefícios para seus colaboradores com planos a partir de 3 vidas e gestão simplificada.",
    color: "#1E88E5", // Azul
    delay: 800,
  },
  {
    icon: <PriceCheckIcon />,
    title: "Melhor Custo-Benefício",
    description:
      "Compare e encontre planos com excelente cobertura e preços que cabem no seu orçamento.",
    color: "#FB8C00", // Laranja
    delay: 1000,
  },
  {
    icon: <SupportAgentIcon />,
    title: "Suporte Especializado",
    description:
      "Consultoria gratuita e atendimento dedicado para ajudar você a escolher o plano ideal.",
    color: "#5E35B1", // Roxo
    delay: 1200,
  },
];

export const FeaturesSection = () => {
  const theme = useTheme();

  return (
    <Box
      id="vantagens"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              display: "block",
              fontWeight: 600,
              color: theme.palette.primary.main,
              mb: 1,
            }}
          >
            Nossas Vantagens
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2rem", md: "2.8rem" },
              color: theme.palette.text.primary,
            }}
          >
            Tudo o que você precisa em um só lugar
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "700px",
              mx: "auto",
            }}
          >
            Oferecemos uma consultoria completa para você encontrar o plano de
            saúde perfeito, com transparência, economia e o melhor atendimento.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Fade in={true} timeout={feature.delay}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    height: "100%",
                    textAlign: "center",
                    borderColor: theme.palette.divider,
                    borderRadius: theme.shape.borderRadius,
                    transition: "border-color 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      borderColor: alpha(
                        feature.color || theme.palette.primary.main,
                        0.5
                      ),
                      boxShadow: `0 6px 18px ${alpha(
                        feature.color || theme.palette.primary.main,
                        0.1
                      )}`,
                      "& .feature-icon": {
                        transform: "scale(1.1)",
                        backgroundColor: alpha(
                          feature.color || theme.palette.primary.main,
                          0.2
                        ),
                      },
                    },
                  }}
                >
                  <StyledIconBox className="feature-icon" color={feature.color}>
                    {feature.icon}
                  </StyledIconBox>
                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
